/**
 * Sample data, so the layout is visible before the socket exists.
 *
 * The desktop app keeps a `SampleDataSource` for exactly this and learned one
 * rule from it worth copying: **it must be obviously sample.** A mockup that
 * signs in normally and then shows people who do not exist is a build somebody
 * runs for days without noticing. So the store says so — `sample` is true on
 * the stub and the shell draws a strip from it.
 *
 * The content is chosen to exercise the shapes rather than to look good: a
 * message with an attachment, a thread reply, a quote, a mention, a `callend`
 * row and a meeting card are each here once, because each is a branch in the
 * transcript.
 */

import type { Conversation, Meeting, Message, Person } from './types';

const HOUR = 3600_000;
const now = Date.now();

export const SAMPLE_ME: Person = {
  id: 'usr_self',
  name: 'Timur Inal',
  handle: 'timurinal',
  presence: 'online'
};

export const SAMPLE_PEOPLE: Person[] = [
  SAMPLE_ME,
  { id: 'usr_ada', name: 'Ada Okonjo', handle: 'ada', presence: 'online' },
  { id: 'usr_rahul', name: 'Rahul Menon', handle: 'rahul', presence: 'busy' },
  { id: 'usr_lena', name: 'Lena Fischer', handle: 'lena', presence: 'away' },
  { id: 'usr_sam', name: 'Sam Whitfield', handle: 'sam', presence: 'offline' }
];

export const SAMPLE_CONVERSATIONS: Conversation[] = [
  {
    id: 'con_ada',
    kind: 'direct',
    title: 'Ada Okonjo',
    topic: '',
    members: ['usr_self', 'usr_ada'],
    ownerId: '',
    unread: 2,
    lastAt: now - 4 * 60_000,
    preview: 'The fire safety module is ready for review.'
  },
  {
    id: 'con_design',
    kind: 'channel',
    title: 'design',
    topic: 'Everything that gets drawn before it gets built',
    members: ['usr_self', 'usr_ada', 'usr_rahul', 'usr_lena'],
    ownerId: 'usr_ada',
    unread: 1,
    mentioned: true,
    lastAt: now - 30 * 60_000,
    preview: '@Timur Inal can you check the rail against what @Rahul Menon drew?'
  },
  {
    id: 'con_launch',
    kind: 'group',
    title: 'Launch week',
    topic: '',
    members: ['usr_self', 'usr_rahul', 'usr_sam'],
    ownerId: 'usr_self',
    unread: 0,
    lastAt: now - 26 * HOUR,
    preview: 'Call ended · 12 min'
  },
  {
    id: 'con_self',
    kind: 'self',
    title: 'Your notes',
    topic: '',
    members: ['usr_self'],
    ownerId: 'usr_self',
    unread: 0,
    lastAt: now - 3 * 24 * HOUR,
    preview: 'Check the runner variables before Monday.'
  }
];

export const SAMPLE_MESSAGES: Record<string, Message[]> = {
  con_ada: [
    {
      id: 'm_1',
      conversationId: 'con_ada',
      seq: 1,
      senderId: 'usr_ada',
      at: now - 40 * 60_000,
      body: 'Morning. I have the fire safety module in a state you can look at.',
      attachments: [],
      kind: 'msg'
    },
    {
      id: 'm_2',
      conversationId: 'con_ada',
      seq: 2,
      senderId: 'usr_ada',
      at: now - 38 * 60_000,
      body: 'Eight lessons, and the assessment at the end still needs its questions.',
      attachments: [
        {
          nodeId: 'nod_f1',
          name: 'Fire safety.pdf',
          mime: 'application/pdf',
          bytes: 4_404_019,
          cloudUrl: 'https://cloud.mauvely.com/s/lnk_sample'
        }
      ],
      kind: 'msg'
    },
    {
      id: 'm_3',
      conversationId: 'con_ada',
      seq: 3,
      senderId: 'usr_self',
      at: now - 20 * 60_000,
      body: 'Reading it now.',
      attachments: [],
      kind: 'msg',
      quote: {
        messageId: 'm_2',
        author: 'Ada Okonjo',
        excerpt: 'Eight lessons, and the assessment at the end still needs its questions.'
      }
    },
    {
      id: 'm_4',
      conversationId: 'con_ada',
      seq: 4,
      senderId: 'usr_ada',
      at: now - 4 * 60_000,
      body: 'The fire safety module is ready for review.',
      attachments: [],
      kind: 'msg'
    }
  ],
  con_design: [
    {
      id: 'm_10',
      conversationId: 'con_design',
      seq: 11,
      senderId: 'usr_rahul',
      at: now - 2 * HOUR,
      body: 'Panels on the ground, 16px gutter. The rail is a panel too.',
      attachments: [],
      kind: 'msg'
    },
    {
      id: 'm_11',
      conversationId: 'con_design',
      seq: 12,
      senderId: 'usr_lena',
      at: now - 110 * 60_000,
      body: 'Then the ground shows between them, which is the whole point of tinting it.',
      attachments: [],
      kind: 'msg',
      replyTo: 'm_10'
    },
    {
      id: 'm_12',
      conversationId: 'con_design',
      seq: 13,
      senderId: 'usr_ada',
      at: now - 30 * 60_000,
      body: '@Timur Inal can you check the rail against what @Rahul Menon drew?',
      attachments: [],
      kind: 'msg',
      mentions: ['usr_self', 'usr_rahul']
    }
  ],
  con_launch: [
    {
      id: 'm_20',
      conversationId: 'con_launch',
      seq: 6,
      senderId: 'usr_self',
      at: now - 27 * HOUR,
      body: '',
      attachments: [],
      kind: 'callend',
      call: { outcome: 'answered', durationSeconds: 736, camera: true }
    },
    {
      id: 'm_21',
      conversationId: 'con_launch',
      seq: 7,
      senderId: 'usr_rahul',
      at: now - 26 * HOUR,
      body: '',
      attachments: [],
      kind: 'meeting',
      meeting: { id: 'mtg_1', title: 'Launch readiness', at: now + 20 * HOUR }
    }
  ],
  con_self: [
    {
      id: 'm_30',
      conversationId: 'con_self',
      seq: 1,
      senderId: 'usr_self',
      at: now - 3 * 24 * HOUR,
      body: 'Check the runner variables before Monday.',
      attachments: [],
      kind: 'msg'
    }
  ]
};

const POLICY = { mic: 'everyone', camera: 'everyone', screen: 'everyone', lobby: 'strangers' } as const;

export const SAMPLE_MEETINGS: Meeting[] = [
  {
    id: 'mtg_1',
    ownerId: 'usr_self',
    title: 'Launch readiness',
    scheduledAt: now + 20 * HOUR,
    status: 'scheduled',
    invitees: [
      { userId: 'usr_rahul', role: 'cohost' },
      { userId: 'usr_sam', role: 'attendee' }
    ],
    conversationId: 'con_launch',
    policy: { ...POLICY },
    recording: false,
    own: true
  },
  {
    id: 'mtg_2',
    ownerId: 'usr_ada',
    title: 'Course review',
    scheduledAt: now - 15 * 60_000,
    status: 'live',
    invitees: [{ userId: 'usr_self', role: 'attendee' }],
    conversationId: '',
    policy: { ...POLICY },
    recording: true,
    own: false
  }
];
